"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { FadeInView } from "@/components/FadeInView";

function readRouteSlotSignature(
  pathname: string,
  root: HTMLElement,
): string {
  const markers = root.querySelectorAll("[data-route-slot]");
  const last =
    markers.length > 0
      ? (markers[markers.length - 1]?.getAttribute("data-route-slot") ?? "")
      : "";
  return `${pathname}:${last}`;
}

export function SiteMainFade({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const containerRef = useRef<HTMLDivElement>(null);
  const prevPathRef = useRef<string | null>(null);
  const prevSigRef = useRef<string | null>(null);
  const [fadeKey, setFadeKey] = useState(0);

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return;
    }

    const sig = readRouteSlotSignature(pathname, root);
    const pathChanged = prevPathRef.current !== pathname;

    if (pathChanged) {
      prevPathRef.current = pathname;
      prevSigRef.current = sig;
      return;
    }

    if (prevSigRef.current === null) {
      prevSigRef.current = sig;
      return;
    }

    if (prevSigRef.current !== sig) {
      prevSigRef.current = sig;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFadeKey((k) => k + 1);
    }
  }, [pathname, children]);

  return (
    <div ref={containerRef} className="min-w-0">
      <FadeInView key={`${pathname}-${fadeKey}`} triggerOnMount className="min-w-0">
        {children}
      </FadeInView>
    </div>
  );
}
